import * as yup from 'yup';
import type { MessageParams } from 'yup/lib/types';

import {
  SIMPLE_CHOICE,
  SIMPLE_CHOICE_META,
  type SimpleChoiceMetaType,
} from '@/features/ChoicesLists/Simple/types';
import { useInputStore } from '@/features/Sparten';
import { getSimpleChoiceMeta } from '@/utils/getSimpleChoiceMeta/getSimpleChoiceMeta';

// when yup v1 is stable check if we can refactor this with test context this.from
export const hasWohneigentumValidator = (value: number): boolean =>
  !(
    useInputStore().versicherungsnehmer.isFirma === false &&
    useInputStore().versicherungsnehmer.hasWohneigentum === true &&
    value === 0
  );

const ageBase = yup.number().default(0);

const testMinorChildAge: yup.TestConfig<number, unknown> = {
  name: 'isValidAge',
  message(params: MessageParams) {
    if (params.value > 17) {
      return 'Das Kind darf maximal 17 Jahre alt sein';
    }
    return 'Das Kind muss mindestens 0 Jahre alt sein';
  },
  test(value, ctx) {
    let metaKey: SimpleChoiceMetaType;

    if (ctx.path.split('.').reverse()[0] === 'alter') {
      metaKey = SIMPLE_CHOICE_META.MINOR_CHILDREN_EIN_KIND;
    } else {
      metaKey = SIMPLE_CHOICE_META.MINOR_CHILDREN_HAS_MEHRERE_KINDER;
    }

    const needCheck = getSimpleChoiceMeta(
      SIMPLE_CHOICE.MINOR_CHILDREN,
      metaKey,
    ).includes(useInputStore().versicherungsnehmer.hasMinderjaehrigeKinder);

    if (needCheck) {
      return value >= 0 && value <= 17;
    }
    return true;
  },
};

export const minderjaehrigeKinderSchema = yup.object({
  alter: ageBase.when({
    is: () =>
      useInputStore().versicherungsnehmer !== undefined &&
      getSimpleChoiceMeta(
        SIMPLE_CHOICE.MINOR_CHILDREN,
        SIMPLE_CHOICE_META.MINOR_CHILDREN_EIN_KIND,
      ).includes(useInputStore().versicherungsnehmer.hasMinderjaehrigeKinder),
    then: ageBase.test(testMinorChildAge),
  }),
  alterAeltestesKind: ageBase.when({
    is: () =>
      useInputStore().versicherungsnehmer !== undefined &&
      getSimpleChoiceMeta(
        SIMPLE_CHOICE.MINOR_CHILDREN,
        SIMPLE_CHOICE_META.MINOR_CHILDREN_HAS_MEHRERE_KINDER,
      ).includes(useInputStore().versicherungsnehmer.hasMinderjaehrigeKinder),
    then: ageBase
      .min(
        yup.ref('alterJuengstesKind'),
        (params) => `Das Kind kann nicht jünger als ${params.min} sein.`,
      )
      .test(testMinorChildAge),
  }),
  alterJuengstesKind: ageBase.when({
    is: () =>
      useInputStore().versicherungsnehmer !== undefined &&
      getSimpleChoiceMeta(
        SIMPLE_CHOICE.MINOR_CHILDREN,
        SIMPLE_CHOICE_META.MINOR_CHILDREN_HAS_MEHRERE_KINDER,
      ).includes(useInputStore().versicherungsnehmer.hasMinderjaehrigeKinder),
    then: ageBase
      .max(
        yup.ref('alterAeltestesKind'),
        (params) => `Das Kind kann nicht älter als ${params.max} sein.`,
      )
      .test(testMinorChildAge),
  }),
});

const mitgliedschaftenSchema = yup.object({
  allgemeinerDeutscherAutomobilClub: yup.boolean().default(false),
  autoClubEuropa: yup.boolean().default(false),
  automobilClubVerkehr: yup.boolean().default(false),
  automobilclubVonDeutschland: yup.boolean().default(false),
  berufsSozialwerk: yup.boolean().default(false),
  berufsstaendischeHandwerkerinnung: yup.boolean().default(false),
  deutscherBeamtenbund: yup.boolean().default(false),
  deutscherBundeswehrverbandFoerdergesellschaft: yup.boolean().default(false),
  einzelhandelsverband: yup.boolean().default(false),
  lehramtsanwaerterprojekt: yup.boolean().default(false),
  unterstuetzungsVorsorgewerk: yup.boolean().default(false),
  verbandBildungErziehung: yup.boolean().default(false),
  vereinteDienstleistungsgewerkschaft: yup.boolean().default(false),
});

const versicherungsnehmerBaseSchema = yup.object({
  beruf: yup.string().default(''),
  berufsstatus: yup.string().default(''),
  hasFuehrerscheinInEUErworben: yup.boolean().default(true),
  hasMinderjaehrigeKinder: yup.string().default(''),
  hasWohnsitzMin5JahreInBrd: yup.boolean().default(false),
  isMitarbeiterAutohersteller: yup.boolean().default(false),
  isVorsteuerAbzugsberechtigt: yup.boolean().default(false),
  minderjaehrigeKinder: minderjaehrigeKinderSchema,
  mitgliedschaften: mitgliedschaftenSchema,
  speziellerArbeitgeber: yup.string().default(''),
  tarifgruppe: yup.string().default(''),
  isFullAge: yup.boolean().default(false),
});

export const versicherungsnehmerSchema = versicherungsnehmerBaseSchema.concat(
  yup.object({
    isFirma: yup.boolean().default(false),
    beruf: yup
      .string()
      .default('')
      .when('isFirma', {
        is: (val: boolean) => !val,
        then: yup.string().required(),
      }),
    branche: yup
      .string()
      .default('')
      .when('isFirma', {
        is: (val: boolean) => !val,
        then: yup.string().required(),
      }),
  }),
);

export const versicherungsnehmerSchemaWithoutFSDate =
  versicherungsnehmerBaseSchema;

export type VersicherungsnehmerSchemaType = yup.Asserts<
  typeof versicherungsnehmerSchema
>;
